import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { ApiService } from 'anatoliapps-angular/services';

import { Channel, Contact, ItemAttributesUpdateResponse, ItemAttributesUpdateResponseSchema, Order, OrderCreateResponse, OrderCreateResponseSchema, OrderDeleteResponse, OrderDeleteResponseSchema, OrderItem, OrderItemSchema, OrderUpdateResponse, OrderUpdateResponseSchema, OrderSchema, OrderStatus, OrderPaymentStatus } from 'hakikat/models';
import { API_CONFIG } from 'hakikat/utils';

export interface OrderCreateInput {
  contact: Contact;

  note?: string;

  orderStatus?: OrderStatus;
  paymentStatus?: OrderPaymentStatus;

  items: {
    pid: string;
    note?: string;
    price?: number;
    quantity?: number;
    attributes?: Record<string, any>;
  }[];
};

export interface OrderUpdateInput extends Partial<OrderCreateInput> { };

@Injectable({
  providedIn: 'root'
})
export class OrderService extends ApiService {

  //#region Endpoints
  readonly #ORDER_ENDPOINT = 'order';
  //#endregion

  //#region Constructor
  constructor() { super(API_CONFIG.apiUrl); }
  //#endregion

  //#region Methods
  public getOrder(oid: string): Observable<Order> {
    return this.get<Order>(`${this.#ORDER_ENDPOINT}/${oid}`).pipe(
      map((response: Order) => OrderSchema.parse(response))
    );
  }

  public getOrders(channel?: Channel): Observable<Order[]> {
    return this.get<Order[]>(`${this.#ORDER_ENDPOINT}${channel ? `?channel=${channel}` : ''}`).pipe(
      map((response: Order[]) => response.map(order => OrderSchema.parse(order)))
    );
  }

  public getOrderItem(iid: string): Observable<OrderItem> {
    return this.get<OrderItem>(`${this.#ORDER_ENDPOINT}/item/${iid}`).pipe(
      map((response: OrderItem) => OrderItemSchema.parse(response))
    );
  }

  public getOrderItems(oid?: string, filters?: Record<string, any>): Observable<OrderItem[]> {
    return this.get<OrderItem[]>(`${this.#ORDER_ENDPOINT}/items/${oid ?? ''}`, filters).pipe(
      map((response: OrderItem[]) => response.map(order => OrderItemSchema.parse(order)))
    );
  }

  public createOrder(input: OrderCreateInput): Observable<OrderCreateResponse> {
    return this.post<OrderCreateResponse>(`${this.#ORDER_ENDPOINT}`, input).pipe(
      map((response: OrderCreateResponse) => OrderCreateResponseSchema.parse(response))
    );
  }

  public updateOrder(id: string, input: OrderUpdateInput): Observable<OrderUpdateResponse> {
    return this.put<OrderUpdateResponse>(`${this.#ORDER_ENDPOINT}/${id}`, input).pipe(
      map((response: OrderUpdateResponse) => OrderUpdateResponseSchema.parse(response))
    );
  }

  public deleteOrder(id: string): Observable<OrderDeleteResponse> {
    return this.delete<OrderDeleteResponse>(`${this.#ORDER_ENDPOINT}/${id}`).pipe(
      map((response: OrderDeleteResponse) => OrderDeleteResponseSchema.parse(response))
    );
  }
  //#endregion

  //#region Order Item Attributes
  public updateOrderItemAttributes(id: string, attributes: Record<string, any>): Observable<ItemAttributesUpdateResponse> {
    return this.put<ItemAttributesUpdateResponse>(`${this.#ORDER_ENDPOINT}/item/${id}/attributes`, { attributes }).pipe(
      map((response: ItemAttributesUpdateResponse) => ItemAttributesUpdateResponseSchema.parse(response))
    );
  }
  //#endregion
}
